import { useSearchParams } from 'react-router';
import {
  APP_URL_PARAM,
  B64_CODE_CHALLENGE_PARAM,
  CLIENT_ID_PARAM,
  CODE_CHALLENGE_METHOD_PARAM,
  REDIRECT_URI_PARAM,
  REQUESTED_PATH_PARAM,
  STATE_PARAM,
} from './Login.constants';
import type { CodeChallengeMethod } from '@/services/login.types';

export interface PkceParams {
  clientId: string;
  redirectUri: string;
  state: string;
  b64CodeChallenge: string;
  codeChallengeMethod: CodeChallengeMethod;
}

export interface AppContext {
  appUrl: string | null;
  requestedPath: string | null;
}

export interface LoginUrlContext {
  pkce: PkceParams | null;
  isPkceValid: boolean;
  appContext: AppContext;
  hasAppUrl: boolean;
}

const nonEmpty = (value: string | null): string | null =>
  value && value.length > 0 ? value : null;

export const deriveLoginUrlContext = (
  searchParams: URLSearchParams,
): LoginUrlContext => {
  const clientId = nonEmpty(searchParams.get(CLIENT_ID_PARAM));
  const redirectUri = nonEmpty(searchParams.get(REDIRECT_URI_PARAM));
  const state = nonEmpty(searchParams.get(STATE_PARAM));
  const b64CodeChallenge = nonEmpty(searchParams.get(B64_CODE_CHALLENGE_PARAM));
  const codeChallengeMethod = nonEmpty(
    searchParams.get(CODE_CHALLENGE_METHOD_PARAM),
  );

  const isPkceValid =
    clientId !== null &&
    redirectUri !== null &&
    state !== null &&
    b64CodeChallenge !== null &&
    codeChallengeMethod !== null;

  const pkce: PkceParams | null = isPkceValid
    ? {
        clientId: clientId as string,
        redirectUri: redirectUri as string,
        state: state as string,
        b64CodeChallenge: b64CodeChallenge as string,
        codeChallengeMethod: codeChallengeMethod as CodeChallengeMethod,
      }
    : null;

  const appUrl = nonEmpty(searchParams.get(APP_URL_PARAM));
  const requestedPath = nonEmpty(searchParams.get(REQUESTED_PATH_PARAM));

  return {
    pkce,
    isPkceValid,
    appContext: { appUrl, requestedPath },
    hasAppUrl: appUrl !== null,
  };
};

const useLoginUrlContext = (): LoginUrlContext => {
  const [searchParams] = useSearchParams();
  return deriveLoginUrlContext(searchParams);
};

export default useLoginUrlContext;
